package com.eladfinish.bible.contest.network.spreadsheet

import com.eladfinish.bible.contest.network.spreadsheet.GoogleSheetRowHeadersAndValues.Companion.toOnlyValues
import kotlinx.serialization.json.Json

// Parsing of Google Sheets' gviz/tq responses — shared by the app (SpreadsheetApi) and the
// website generator, which both read the same public spreadsheets.

/** The gviz/tq endpoint for a spreadsheet (and optionally one of its sheets, by gid). */
fun gvizUrl(spreadsheetId: String, sheetId: String = ""): String =
    "https://docs.google.com/spreadsheets/d/$spreadsheetId/gviz/tq?gid=$sheetId"

private val sheetJson = Json {
    ignoreUnknownKeys = true
    explicitNulls = false
}

// Google's gviz/tq endpoint wraps its JSON differently depending on whether the request sent the
// "X-DataSource-Auth" header: with it, the response is prefixed with ")]}'\n" (XSSI protection);
// without it, the response is a JSONP callback: "/*O_o*/\ngoogle.visualization.Query.setResponse(...)".
// The web target can't send that custom header (it's not an allowed cross-origin header on this
// endpoint, so the browser blocks the request with a CORS preflight failure — see
// SpreadsheetApiImpl), so it always gets the JSONP-wrapped form; strip whichever wrapper is present.
private const val JSONP_PREFIX = "/*O_o*/\ngoogle.visualization.Query.setResponse("

fun String.toGoogleSheetTableRawJson(): String =
    when {
        startsWith(")]}'\n") -> removePrefix(")]}'\n")
        startsWith(JSONP_PREFIX) -> removePrefix(JSONP_PREFIX).removeSuffix(");")
        else -> this
    }

fun List<GoogleSheetRowHeadersAndValues>.valuesWithoutColumnsTest(): List<List<String>> =
    this.toOnlyValues()

fun String.toSpreadsheetMatrixWithHeadersTest(): List<GoogleSheetRowHeadersAndValues> =
    sheetJson.decodeFromString<GoogleSheetTable>(this.toGoogleSheetTableRawJson())
        .let {
            it.googleSheetTableData.columns to it.googleSheetTableData.rowsWithoutDummies.withIndex()
        }
        .let {
            val googleSheetIndexedContentLists: List<GoogleSheetIndexedContent> =
                it.second.map { row -> GoogleSheetIndexedContent(index = row.index, googleSheetContent = row.value) }
            val listList: List<List<Pair<GoogleSheetColumnData, String>>> =
                googleSheetIndexedContentLists.map { googleSheetIndexedContent: GoogleSheetIndexedContent ->
                    val list: List<String> = googleSheetIndexedContent.googleSheetContent.list.map { item -> item?.getValue().orEmpty() }
                    it.first.zip(list)
                }
            listList.withIndex().toList()
        }
        .let { list: List<IndexedValue<List<Pair<GoogleSheetColumnData, String>>>> ->
            list.map {
                GoogleSheetRowHeadersAndValues(
                    list = it.value,
                    index = it.index,
                )
            }
        }

fun String.toSpreadsheetMatrixTest(): List<List<String>> =
    this.toSpreadsheetMatrixWithHeadersTest()
        .toOnlyValues()
